import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import {
  User as FirebaseUser,
  onAuthStateChanged,
  signOut,
  signInWithCustomToken,
} from "firebase/auth";
import { auth } from "../firebase";

const IS_DEV_AUTH = import.meta.env.VITE_USE_DEV_AUTH === "true";

interface DevUser {
  uid: string;
  email: string;
  role: string;
}

interface AuthContextType {
  user: FirebaseUser | null;
  devUser: DevUser | null;
  logout: () => Promise<void>;
  loading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [devUser, setDevUser] = useState<DevUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (IS_DEV_AUTH) {
      const initDevAuth = async () => {
        try {
          const response = await fetch("/api/auth/session-verify", {
            method: "POST",
            credentials: "include",
          });
          if (response.ok) {
            const data = await response.json();
            // console.log("SESSION VERIFY RESPONSE:", data);
            // console.log("CUSTOM TOKEN:", data.customToken);

            setDevUser(data.user);
          }
        } catch (e) {
          console.error("Dev auth init failed:", e);
        } finally {
          setLoading(false);
        }
      };
      initDevAuth();
      return;
    }

    const initializeAuth = async () => {
      try {
        const response = await fetch("/api/auth/session-verify", {
          method: "POST",
          credentials: "include",
        });

        if (response.ok) {
          const data = await response.json();

          await signInWithCustomToken(auth, data.customToken);
        } else {
          if (response.status !== 401) {
            setError("認証状態を確認できませんでした。");
          }
          setLoading(false);
          return;
        }
      } catch (err) {
        console.error("Session verification failed:", err);
        setError("認証状態を確認できませんでした。");
        setLoading(false);
        return;
      }
    };

    initializeAuth();

    const unsubscribe = onAuthStateChanged(auth, (fbUser) => {
      setUser(fbUser);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const logout = async () => {
    if (IS_DEV_AUTH) {
      setDevUser(null);
      setLoading(false);
      return;
    }
    try {
      await fetch("/api/auth/session-logout", {
        method: "POST",
        credentials: "include",
      });
      await signOut(auth);
      setUser(null);
      window.location.href = "https://yakizakana.net/login";
    } catch (err) {
      console.error("Logout failed:", err);
      window.location.href = "https://yakizakana.net/login";
    }
  };

  const value = {
    user,
    devUser,
    logout,
    loading,
    error,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
